import { useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import {
  GET_UNREAD_USER_NOTIFICATIONS,
  GET_USER_NOTIFICATION_STATS,
  GET_USER_NOTIFICATIONS,
} from "@/graphql/notifications";
import type { StandardError } from "@/lib/error/error-utils";
import { transformApolloError } from "@/lib/error/error-utils";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { useSalonStore } from "@/lib/store/salon";

interface UseNotificationQueryOptions {
  salonId?: string | null;
  first?: number;
}

export const useNotificationQuery = (options: UseNotificationQueryOptions = {}) => {
  const { salonId: propSalonId, first = 20 } = options;

  const { user } = useAuthStore();
  const { currentSalon } = useSalonStore();

  const salonId = propSalonId !== undefined ? propSalonId : currentSalon?.id || null;

  const {
    data,
    loading,
    error: apolloError,
    refetch,
    fetchMore,
  } = useQuery(GET_USER_NOTIFICATIONS, {
    variables: {
      salonId,
      first,
    },
    skip: salonId === null || !user?.id,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const error = useMemo((): StandardError | null => {
    if (!apolloError) return null;
    return transformApolloError(apolloError);
  }, [apolloError]);

  const retry = useCallback(() => {
    refetch();
  }, [refetch]);

  // Calculate hasMore from cursor pagination data
  const hasMore = useMemo(() => {
    return data?.getNotifications?.pageInfo?.hasNextPage || false;
  }, [data]);

  return {
    data,
    loading,
    error,
    retry,
    refetch,
    fetchMore,
    hasMore,
  };
};

export const useUnreadNotificationQuery = (limit = 10) => {
  const { user } = useAuthStore();
  const { currentSalon } = useSalonStore();

  const salonId = currentSalon?.id || null;

  const {
    data,
    loading,
    error: apolloError,
    refetch,
  } = useQuery(GET_UNREAD_USER_NOTIFICATIONS, {
    variables: {
      salonId,
      first: limit,
    },
    skip: salonId === null || !user?.id,
    pollInterval: 15000,
    errorPolicy: "all",
  });

  const error = useMemo((): StandardError | null => {
    if (!apolloError) return null;
    return transformApolloError(apolloError);
  }, [apolloError]);

  const retry = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    data,
    loading,
    error,
    retry,
    refetch,
  };
};

export const useNotificationStatsQuery = () => {
  const { user } = useAuthStore();
  const { currentSalon } = useSalonStore();

  const salonId = currentSalon?.id || null;

  const {
    data,
    loading,
    error: apolloError,
    refetch,
  } = useQuery(GET_USER_NOTIFICATION_STATS, {
    variables: {
      salonId,
    },
    skip: salonId === null || !user?.id,
    pollInterval: 30000,
    errorPolicy: "all",
  });

  const error = useMemo((): StandardError | null => {
    if (!apolloError) return null;
    return transformApolloError(apolloError);
  }, [apolloError]);

  const retry = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    data,
    loading,
    error,
    retry,
    refetch,
  };
};
